import { Familiar } from "@/game/entities/Familiar";

/**
 * ゴーレム（使い魔）のSVG描画
 *
 * CSS: field.css の .golem-svg セクション
 * 構造: <svg class="golem-svg" viewBox="0 0 32 32" data-direction>
 *         <defs>…clipPaths…</defs>
 *         <g class="dir-down">…</g>
 *         <g class="dir-up">…</g>
 *         <g class="dir-right">…</g>
 *         <g class="dir-left">…</g>
 *       </svg>
 *
 * Familiar.onChange を購読し、位置・向きの変化に応じてDOMを更新。
 * 位置の更新は onMove コールバック経由でタイルセルの付け替えを依頼する。
 */
export class FamiliarRenderer {
  private element: SVGSVGElement;
  /** clipPath ID の衝突を防ぐインスタンス固有サフィックス */
  private readonly uid = Math.random().toString(36).slice(2, 7);

  /**
   * 位置変更時のコールバック。
   * TileMapRendererがタイルセル間のDOM移動を実行する。
   */
  onMove?: (gridX: number, gridY: number) => void;

  constructor(private familiar: Familiar) {
    this.element = this.createElement();
    this.updateDirection();

    familiar.onChange = () => {
      this.updateDirection();
      this.onMove?.(familiar.gridX, familiar.gridY);
    };
  }

  private createElement(): SVGSVGElement {
    // SVG名前空間を正しく保つための中間コンテナ（最終DOMには挿入しない）
    const div = document.createElement("div");
    const u = this.uid;
    div.innerHTML = `<svg class="golem-svg" aria-hidden="true" focusable="false" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <clipPath id="g-${u}-d-el"><circle cx="12" cy="14" r="2.5"/></clipPath>
    <clipPath id="g-${u}-d-er"><circle cx="20" cy="14" r="2.5"/></clipPath>
    <clipPath id="g-${u}-r-e"><circle cx="21" cy="13" r="2.5"/></clipPath>
    <clipPath id="g-${u}-l-e"><circle cx="11" cy="13" r="2.5"/></clipPath>
  </defs>

  <g class="dir-down">
    <ellipse cx="16" cy="31" rx="8" ry="1.3" fill="#000" opacity="0.2"/>
    <g class="golem-upper">
      <path d="M 11,22 Q 8,26 7,27 Q 7,28 8,28 L 24,28 Q 25,28 25,27 Q 24,26 21,22 Z" fill="#7a4028"/>
      <rect x="5" y="4" width="22" height="18" rx="6" fill="#9a6040"/>
      <path d="M 6,7 Q 16,5 26,7 L 22,5 Q 16,4 10,5 Z" fill="#c07848" opacity="0.5"/>
      <path d="M 19,5 L 21,11 L 18,16" stroke="#4a1c08" stroke-width="0.8" fill="none" opacity="0.5" stroke-linecap="round"/>
      <circle cx="12" cy="14" r="2.5" fill="#0a0608"/>
      <circle cx="12.8" cy="13.2" r="0.9" fill="white" opacity="0.8"/>
      <circle cx="20" cy="14" r="2.5" fill="#0a0608"/>
      <circle cx="20.8" cy="13.2" r="0.9" fill="white" opacity="0.8"/>
      <rect class="lid-l" clip-path="url(#g-${u}-d-el)" x="9" y="11" width="7" height="7" fill="#9a6040"/>
      <rect class="lid-r" clip-path="url(#g-${u}-d-er)" x="17" y="11" width="7" height="7" fill="#9a6040"/>
      <line x1="13" y1="19.5" x2="19" y2="19.5" stroke="#4a1c08" stroke-width="1" stroke-linecap="round" opacity="0.7"/>
    </g>
    <rect class="golem-foot" x="10" y="28" width="4" height="3" rx="0.8" fill="#5a2c18"/>
    <rect class="golem-foot" x="18" y="28" width="4" height="3" rx="0.8" fill="#5a2c18"/>
  </g>

  <g class="dir-up">
    <ellipse cx="16" cy="31" rx="8" ry="1.3" fill="#000" opacity="0.2"/>
    <g class="golem-upper">
      <path d="M 11,22 Q 8,26 7,27 Q 7,28 8,28 L 24,28 Q 25,28 25,27 Q 24,26 21,22 Z" fill="#7a4028"/>
      <rect x="5" y="4" width="22" height="18" rx="6" fill="#9a6040"/>
      <path d="M 6,7 Q 16,5 26,7 L 22,5 Q 16,4 10,5 Z" fill="#c07848" opacity="0.5"/>
      <path d="M 13,5 L 11,12 L 14,18" stroke="#4a1c08" stroke-width="0.8" fill="none" opacity="0.5" stroke-linecap="round"/>
      <path d="M 11,12 L 8,14" stroke="#4a1c08" stroke-width="0.8" fill="none" opacity="0.5" stroke-linecap="round"/>
      <path d="M 21,5 L 20,13" stroke="#4a1c08" stroke-width="0.8" fill="none" opacity="0.5" stroke-linecap="round"/>
    </g>
    <rect class="golem-foot" x="10" y="28" width="4" height="3" rx="0.8" fill="#5a2c18"/>
    <rect class="golem-foot" x="18" y="28" width="4" height="3" rx="0.8" fill="#5a2c18"/>
  </g>

  <g class="dir-right">
    <ellipse cx="16" cy="31" rx="7" ry="1.3" fill="#000" opacity="0.2"/>
    <g class="golem-upper">
      <path d="M 12,22 Q 10,26 9,27 Q 9,28 10,28 L 22,28 Q 23,28 23,27 Q 22,26 20,22 Z" fill="#7a4028"/>
      <rect x="9" y="4" width="16" height="18" rx="5" fill="#9a6040"/>
      <path d="M 10,7 Q 17,5 24,7 L 21,5 Q 17,4 13,5 Z" fill="#c07848" opacity="0.5"/>
      <path d="M 12,5 L 11,13" stroke="#4a1c08" stroke-width="0.8" fill="none" opacity="0.5" stroke-linecap="round"/>
      <circle cx="21" cy="13" r="2.5" fill="#0a0608"/>
      <circle cx="21.8" cy="12.2" r="0.9" fill="white" opacity="0.8"/>
      <rect class="lid-r" clip-path="url(#g-${u}-r-e)" x="18" y="10" width="7" height="7" fill="#9a6040"/>
      <line x1="18" y1="19.5" x2="22" y2="19.5" stroke="#4a1c08" stroke-width="1" stroke-linecap="round" opacity="0.7"/>
    </g>
    <rect class="golem-foot" x="15" y="28" width="4" height="3" rx="0.8" fill="#5a2c18"/>
  </g>

  <g class="dir-left">
    <ellipse cx="16" cy="31" rx="7" ry="1.3" fill="#000" opacity="0.2"/>
    <g class="golem-upper">
      <path d="M 20,22 Q 22,26 23,27 Q 23,28 22,28 L 10,28 Q 9,28 9,27 Q 10,26 12,22 Z" fill="#7a4028"/>
      <rect x="7" y="4" width="16" height="18" rx="5" fill="#9a6040"/>
      <path d="M 8,7 Q 15,5 22,7 L 19,5 Q 15,4 11,5 Z" fill="#c07848" opacity="0.5"/>
      <path d="M 19,5 L 21,12 L 18,15" stroke="#4a1c08" stroke-width="0.8" fill="none" opacity="0.5" stroke-linecap="round"/>
      <path d="M 21,12 L 23,14" stroke="#4a1c08" stroke-width="0.8" fill="none" opacity="0.5" stroke-linecap="round"/>
      <path d="M 9,5 L 12,11 L 9,16" stroke="#4a1c08" stroke-width="0.8" fill="none" opacity="0.5" stroke-linecap="round"/>
      <circle cx="11" cy="13" r="2.5" fill="#0a0608"/>
      <circle cx="10.2" cy="12.2" r="0.9" fill="white" opacity="0.8"/>
      <rect class="lid-l" clip-path="url(#g-${u}-l-e)" x="8" y="10" width="7" height="7" fill="#9a6040"/>
      <line x1="10" y1="19.5" x2="14" y2="19.5" stroke="#4a1c08" stroke-width="1" stroke-linecap="round" opacity="0.7"/>
    </g>
    <rect class="golem-foot" x="13" y="28" width="4" height="3" rx="0.8" fill="#5a2c18"/>
  </g>
</svg>`;
    const el = div.firstElementChild;
    if (!(el instanceof SVGSVGElement)) {
      throw new Error("FamiliarRenderer: SVG element の生成に失敗しました");
    }
    return el;
  }

  private updateDirection(): void {
    this.element.setAttribute("data-direction", this.familiar.direction);
  }

  getElement(): SVGSVGElement {
    return this.element;
  }

  getGridX(): number {
    return this.familiar.gridX;
  }

  getGridY(): number {
    return this.familiar.gridY;
  }
}
